import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Minus, ArrowRight } from "lucide-react";
import { Reveal, SectionHeader } from "@/components/Reveal";

const CHALLENGES = [
    {
        number: "01",
        title: "Legacy infrastructure is holding the business back",
        summary: "High maintenance costs, slow release cycles and systems nobody dares to touch.",
        approach: ["Assess the current environment and dependencies", "Prioritize modernization, migration or refactoring", "Build a controlled transition plan", "Document ownership, operations and recovery procedures"],
        outcomes: ["A clearer modernization roadmap", "Reduced operational friction", "Improved scalability and supportability", "A controlled path away from legacy constraints"],
        capabilities: ["Cloud & Infrastructure", "Managed IT Services", "Technology Consulting"],
        path: "/services/cloud-infrastructure",
    },
    {
        number: "02",
        title: "Cloud complexity keeps compounding",
        summary: "Sprawling estates, unpredictable bills and architectures that grew without a plan.",
        approach: ["Assess workloads, architecture and dependencies", "Design cloud architecture around business and technical requirements", "Plan and execute migration where appropriate", "Apply security, governance, cost visibility and optimization practices", "Operate and continuously improve the environment"],
        outcomes: ["Better visibility into cloud environments", "Stronger cost governance", "More consistent architecture and operations", "A cloud model aligned to business priorities"],
        capabilities: ["Cloud & Infrastructure", "Cybersecurity", "Managed IT Services"],
        path: "/services/cloud-infrastructure",
    },
    {
        number: "03",
        title: "Cybersecurity threats are outpacing defenses",
        summary: "Expanding attack surfaces, fragmented tooling and mounting compliance pressure.",
        approach: ["Establish visibility across identity, endpoints, networks and cloud", "Connect monitoring, detection, triage and response workflows", "Apply identity, endpoint and cloud security controls", "Apply zero-trust principles where appropriate to the environment"],
        outcomes: ["Improved security visibility", "Faster identification and structured response", "Stronger operational controls", "A coherent security operating lifecycle"],
        capabilities: ["Cybersecurity", "Digital Workplace", "Managed IT Services"],
        path: "/services/cybersecurity",
    },
    {
        number: "04",
        title: "Data is fragmented and AI-ready nowhere",
        summary: "Siloed data, no governance, and AI pilots that never reach production.",
        approach: ["Map data sources, ownership and dependencies", "Design an integrated data architecture", "Establish pipelines, governance, access controls and data-quality practices", "Prepare cloud data platforms for analytics and AI workloads", "Create a repeatable path from experimentation to governed production"],
        outcomes: ["A structured data foundation", "Clearer data ownership and governance", "Real readiness for AI initiatives", "A repeatable path toward production use cases"],
        capabilities: ["AI & Data Solutions", "Cloud & Infrastructure", "Technology Consulting"],
        path: "/services/ai-data-solutions",
    },
    {
        number: "05",
        title: "Skills gaps slow every initiative",
        summary: "Scarce specialists, overloaded teams and roadmaps that slip quarter after quarter.",
        approach: ["Identify capability and coverage gaps", "Extend internal teams with defined technical services", "Add engineering and operations capacity across cloud, infrastructure, network and security", "Use documented escalation, reporting and service ownership", "Scale coverage as priorities change"],
        outcomes: ["Additional senior technical capacity", "More consistent operational coverage", "Reduced pressure on internal teams", "A scalable way to execute priority initiatives"],
        capabilities: ["Managed IT Services", "Cloud & Infrastructure", "Cybersecurity"],
        path: "/services/managed-it-services",
    },
];

const Challenges = () => {
    const [active, setActive] = useState(null);
    const selected = active === null ? null : CHALLENGES[active];

    return (
        <section data-testid="challenges-section" className="bg-grid-light t-bg-alt py-14 t-heading md:py-24">
            <div className="container-x">
                <Reveal>
                    <SectionHeader
                        eyebrow="The Challenge"
                        title="Technology is changing faster than most organizations can adapt"
                        intro="Legacy systems, cloud complexity, security threats and the pressure to adopt AI are converging at once. Most enterprises don't lack ambition — they lack a clear path through the complexity."
                    />
                    <div className="mt-8 flex flex-wrap items-baseline gap-x-6 gap-y-2">
                        <p className="border-l-2 border-electric pl-5 font-display text-xl font-semibold leading-snug t-heading md:text-2xl">
                            We turn complexity into clarity.
                        </p>
                        <p className="text-sm t-soft">Select a challenge to see how Varasun approaches it.</p>
                    </div>
                </Reveal>

                <div className="mt-12 border-y t-border" role="list">
                    {CHALLENGES.map((c, i) => (
                        <Reveal key={c.number} delay={i * 0.05}>
                            <div className="border-b t-border last:border-b-0" role="listitem">
                                <button
                                    type="button"
                                    data-testid={`challenge-card-${c.number}`}
                                    onClick={() => setActive(active === i ? null : i)}
                                    aria-expanded={active === i}
                                    aria-controls={`challenge-detail-${c.number}`}
                                    className={`group grid w-full grid-cols-[3rem_1fr_2rem] items-start gap-3 py-7 text-left transition-colors md:grid-cols-[4rem_1.2fr_1.4fr_3rem] ${
                                        active === i ? "t-cardbg" : "t-hoverbg"
                                    }`}
                                >
                                    <span className="font-mono text-sm font-semibold text-electric">{c.number}</span>
                                    <span className="font-display text-lg font-semibold leading-snug t-heading transition-colors t-gh-accent md:text-xl">
                                        {c.title}
                                    </span>
                                    <span className="hidden text-sm leading-relaxed t-soft md:block">{c.summary}</span>
                                    <span className="flex h-8 w-8 items-center justify-center justify-self-end rounded-full border t-border t-faint transition-colors group-hover:border-electric group-hover:text-electric">
                                        {active === i ? <Minus size={15} /> : <Plus size={15} />}
                                    </span>
                                </button>

                                <div
                                    id={`challenge-detail-${c.number}`}
                                    className={`grid transition-[grid-template-rows] duration-500 ease-out ${
                                        active === i ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                                    }`}
                                >
                                    <div className="overflow-hidden">
                                        {selected && active === i && (
                                            <div className="grid gap-8 t-cardbg px-6 py-9 md:grid-cols-3 md:px-10" data-testid={`challenge-detail-panel-${c.number}`}>
                                                <div>
                                                    <p className="flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] t-accent">
                                                        <span className="h-3.5 w-0.5 rounded-full bg-electric" aria-hidden="true" />
                                                        Our Approach
                                                    </p>
                                                    <ul className="mt-4 space-y-2.5">
                                                        {c.approach.map((a) => (
                                                            <li key={a} className="flex items-start gap-2.5 text-sm leading-relaxed t-soft">
                                                                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-electric" />
                                                                {a}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                                <div>
                                                    <p className="flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] t-accent">
                                                        <span className="h-3.5 w-0.5 rounded-full bg-electric" aria-hidden="true" />
                                                        Business Outcomes
                                                    </p>
                                                    <ul className="mt-4 space-y-2.5">
                                                        {c.outcomes.map((o) => (
                                                            <li key={o} className="flex items-start gap-2.5 text-sm leading-relaxed t-soft">
                                                                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600" />
                                                                {o}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                                <div>
                                                    <p className="flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] t-accent">
                                                        <span className="h-3.5 w-0.5 rounded-full bg-electric" aria-hidden="true" />
                                                        Relevant Capabilities
                                                    </p>
                                                    <div className="mt-4 flex flex-wrap gap-2.5">
                                                        {c.capabilities.map((cap) => (
                                                            <span
                                                                key={cap}
                                                                className="rounded-full border t-accent-border t-chip px-3.5 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-wider t-accent shadow-[0_2px_10px_-4px_rgba(0,184,240,0.35)]"
                                                            >
                                                                {cap}
                                                            </span>
                                                        ))}
                                                    </div>
                                                    <Link
                                                        to={c.path}
                                                        data-testid={`challenge-explore-${c.number}`}
                                                        className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold t-accent transition-[gap,color] duration-300 hover:gap-2.5 t-hover-accent"
                                                    >
                                                        Explore capability <ArrowRight size={15} />
                                                    </Link>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Challenges;
